// Briefing masthead date from an ISO timestamp → "Saturday, June 20" (no year).
// Used by the today surface and the briefing-history detail (which reuses it).
export function formatBriefingDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// Briefing-history LIST date from a 'YYYY-MM-DD' calendar string → "Sat, Jun 20, 2026".
// Parsed at local midnight to avoid a timezone day-shift; distinct from the ISO
// masthead formatter above (different input + output), so it gets its own name.
export function formatBriefingCalendarDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// A note's CONTENT date → "Jul 15" this year, "Feb 6, 2023" in any other.
//
// Absolute and UTC-pinned, deliberately NOT relativeTime(): Ask threads are resumable via #/ask/<uuid>,
// so a "6d ago" chip silently rots the moment the user reopens a thread next week, whereas "Jul 15" never
// does. That is a conscious break from the house relative-time style — correctness over consistency, for
// the one surface whose render outlives the read.
//
// THE YEAR IS NOT OPTIONAL on an older note. The knowledge corpus reaches back to 2023 (measured on prod:
// 225 of 2,551 notes predate this year), and a bare "Feb 6" on a 2023 ChatGPT thread is indistinguishable
// from this February — which would make a date chip actively imply recency for the single stalest material
// we hold, the exact opposite of why it exists. Same-year dates stay short because that is the common case
// and the eyebrow is 9px.
//
// UTC because capture timestamps are ISO UTC: a machine-local render shifts the date across midnight
// boundaries (and makes unit tests timezone-dependent) — the same pin ConversationDetailSurface uses. The
// current-year comparison is UTC on both sides so it can never disagree with the rendered date.
// Accepts a full ISO timestamp or a bare YYYY-MM-DD; returns null on absent/unparseable input so callers
// render nothing rather than "Invalid Date".
export function formatSourceDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const thisYear = parsed.getUTCFullYear() === new Date().getUTCFullYear();
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(thisYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  // W4 (defensive): a FUTURE timestamp must never read as "just now". A future-dated row (e.g. a
  // calendar meeting's scheduled start) was the "just now"/"active now" bug on the Live board; after
  // the backend meeting exclusion nothing future reaches here, but this protects every future reader.
  if (diff < 0) return 'soon';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
